<script setup lang="ts">

import {computed, onMounted, ref, PropType} from "vue";
import yapi from "@/lib/yapi";
import {isYapiError, YapiError} from "@YenzaCT/sdk";
import {Curricula} from "@YenzaCT/sdk/src/yapi/objects/user/Curricula";
import {useI18n} from "vue-i18n";
import {useGlobalStore} from "@/store";
import {IUserSubjectChoicesReportData} from "@/pages/Insights/ReportsDetailPage/reportTypes";

const {t} = useI18n();
const store = useGlobalStore();

const schoolCurricula = ref<Curricula>();

const props = defineProps({
  reportData: {
    type: Array as PropType<IUserSubjectChoicesReportData[]>,
    required: true
  },
  curriculum: {
    type: String,
    required: true,
    default: "national_senior_certificate"
  },
  loadingData: {
    type: Boolean,
    required: true
  }
});

function getSubjectValue(key: string, item: IUserSubjectChoicesReportData) {
  if (!props.curriculum)
    return "No Curriculum";

  if (!item)
    return "No Value";

  if (!item.subjects || !item.subjects[key]) {
    return "Not Selected";
  } else {
    return t(`curriculums.${props.curriculum}.subjects.${item.subjects[key]}`);
  }
}

/**
 * Sets the headers for the report
 */
const headers = computed(() => {
  const baseHeaders = [
    {
      title: t("components.ReportsDetailPage.holland_code_headers.full_name"),
      key: "fullName"
    },
    {
      title: "Tenant",
      key: "tenant"
    },
  ];

  if (curriculumHeaders.value)
    return baseHeaders.concat(curriculumHeaders.value);
  return baseHeaders;
});

async function getSchoolCurricula() {
  try {
    store.networkBusy = true;

    const res = await yapi.curriculum.list();
    schoolCurricula.value = res.data;
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
  }
}

const curriculumHeaders = computed(() => {
  if (!schoolCurricula.value)
    return [];

  if (props.curriculum)
    return Object.values(schoolCurricula.value[props.curriculum].subjectGroups).map((subjectGroup) => ({
      title: t(`curriculums.${props.curriculum}.subject_groups.${subjectGroup.slug}`),
      key: subjectGroup.slug
    }));
  return [];
});

onMounted(async () => {
  await getSchoolCurricula();
});
</script>

<template>
  <v-data-table
    :items="reportData"
    :headers="headers"
    :loading="loadingData"
    :items-per-page="50"
  >
    <template v-slot:item.fullName="{ item }">
      <span class="font-weight-bold">{{ item.fullName }}</span>

      <div>
        <div v-if="item.email">
          <v-icon size="x-small">far fa-envelope</v-icon>
          {{ item.email }}
        </div>
        <div v-if="item.phone">
          <v-icon size="x-small">far fa-phone</v-icon>
          {{ item.phone }}
        </div>
      </div>
    </template>

    <template v-slot:item.tenant="{ item }">
      <div class="py-4">
        <div class="font-weight-bold text-blue-grey-darken-1 pb-1">{{ item.tenant.title }}</div>
        <div>
          <v-chip v-if="item.institution.title" size="x-small" class="font-weight-bold mr-3">
            <v-icon color="grey" class="mr-2">far fa-building</v-icon>
            {{ item.institution.title }}
          </v-chip>
          <v-chip v-if="item.cohort.title" size="x-small" class="font-weight-bold">
            <v-icon color="grey" class="mr-2">far fa-graduation-cap</v-icon>
            {{ item.cohort.title }}
          </v-chip>
        </div>
      </div>
    </template>

    <template v-for="header in curriculumHeaders" v-slot:[`item.${header.key}`]="{ item }" :key="header.key">
      <v-chip class="font-weight-bold">
        {{ getSubjectValue(header.key, item) }}
      </v-chip>
    </template>
  </v-data-table>
</template>

<style scoped>

</style>
